import { computed, DestroyRef, Inject, Injectable, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, Observable, of } from 'rxjs';

import {
	AbstractAccountSettingsService,
	AbstractProfileService,
	ACCOUNT_SETTINGS_SERVICE_INJECTOR,
	PROFILE_SERVICE_INJECTOR,
} from '@campaign-portal/components-library';
import { User, UserNotifications } from '@campaign-portal/namespace/common/user';
import { NotificationApi, NotificationState } from '@campaign-portal/contracts/account-settings/notification';

@Injectable({ providedIn: 'root' })
export class AlarisNotificationAdapter extends NotificationApi {
	private readonly raw = signal<User | null>(null);

	readonly state: Signal<NotificationState | null> = computed(() => {
		const u = this.raw();
		return u ? AlarisNotificationAdapter.toState(u) : null;
	});

	constructor(
		@Inject(ACCOUNT_SETTINGS_SERVICE_INJECTOR)
		private readonly account: AbstractAccountSettingsService,
		@Inject(PROFILE_SERVICE_INJECTOR)
		private readonly profile: AbstractProfileService,
		destroyRef: DestroyRef
	) {
		super();
		this.profile.user$.pipe(takeUntilDestroyed(destroyRef)).subscribe((u) => {
			if (u?.id) {
				this.raw.set(u);
			}
		});
	}

	load(): Observable<NotificationState> {
		const current = this.raw();
		if (current && this.profile.user.isAccountDetailsLoaded) {
			return of(AlarisNotificationAdapter.toState(current));
		}
		return this.account.load().pipe(
			map((user) => {
				if (user) {
					this.raw.set(user);
					return AlarisNotificationAdapter.toState(user);
				}
				throw new Error('Failed to load notifications');
			})
		);
	}

	update(notifications: UserNotifications): Observable<void> {
		const current = this.raw();
		if (!current) {
			throw new Error('User not loaded yet');
		}
		const patched: User = { ...current, notifications };
		return this.account.update(patched).pipe(
			map((resp) => {
				const updated = resp.Data?.at(0);
				if (resp.Success && updated) {
					this.profile.register(this.profile.convert(updated));
				}
			})
		);
	}

	private static toState(u: User): NotificationState {
		return {
			role: u.role,
			isOwner: Boolean(u.details?.isOwner),
			notifications: u.notifications,
		};
	}
}
